import { Button } from "@/components/ui/button";
import { Plan } from "../models/plan";
import { Subscription } from "../models/subscription";
import PlanFeatures from "./plan-features";

type Props = {
	currentSubscription?: Subscription | null;
	currentPlan?: Plan | null;
	plan: Plan;
	plansCount: number;
	selectedPlanId: number | undefined;
	onSelect: (plan: Plan) => void;
	isRecommended?: boolean;
};

export default function PlanCard({
	currentSubscription,
	currentPlan,
	plan,
	plansCount,
	selectedPlanId,
	onSelect,
	isRecommended = false,
}: Props) {
	const isSelected = plan.id === selectedPlanId;
	const isCurrentPlan = currentPlan?.id === plan.id;

	let containerClassName = `relative z-10 flex flex-col gap-8 p-6 md:w-1/${plansCount} max-sm:bg-white rounded-[24px] transition-all duration-300`;
	let containerStyles: React.CSSProperties = {};
	let titleClassName = "web-heading-h3-medium min-h-20";
	let priceClassName = "web-heading-h3";
	let priceCycleClassName = "web-body-b1 text-gray-500";
	let whatsIncludedClassName = "web-body-b1-medium text-gray-700";

	if (isRecommended && !isSelected && !isCurrentPlan) {
		containerClassName = `relative z-10 flex flex-col gap-8 p-6 md:w-1/${plansCount} max-sm:bg-white rounded-[24px] transition-all duration-300 ring-2 ring-blue-500/40`;
		containerStyles = {
			background:
				"linear-gradient(180deg, rgba(0,64,150,0.06) 0%, rgba(71,0,150,0.06) 100%)",
		};
	}

	if (isSelected) {
		containerClassName = `relative z-10 flex flex-col gap-8 p-6 rounded-[24px] md:w-1/${plansCount} md:-translate-y-12 transition-all duration-300`;
		containerStyles = {
			background:
				"radial-gradient(171.4% 138.54% at 80.57% 8.23%, rgba(46, 219, 202, 0.30) 18.51%, rgba(8, 50, 46, 0.00) 100%), linear-gradient(180deg, #004096 -29.98%, #470096 122.31%)",
			backgroundBlendMode: "plus-lighter, normal",
			boxShadow: "0px 15px 28.5px 0px rgba(0, 64, 150, 0.20)",
		} as const;
		titleClassName = "web-heading-h3-medium min-h-20 text-white";
		priceClassName = "web-heading-h3 text-white";
		priceCycleClassName = "web-body-b1 text-white-80";
		whatsIncludedClassName = "web-body-b1-medium text-white";
	}

	if (isCurrentPlan) {
		containerClassName = `relative z-10 flex flex-col gap-8 p-6 md:w-1/${plansCount} bg-green-300-10 rounded-[24px] border-1 border-blue-500-10 transition-all duration-300`;
	}

	let buttonText = "Select plan";
	if (isSelected) {
		buttonText = "Selected plan";
	}
	if (currentPlan) {
		buttonText = "Change plan";
	}

	return (
		<div className={containerClassName} style={containerStyles}>
			{isRecommended && !isCurrentPlan && (
				<div className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-4 py-1 text-xs font-bold text-white"
					style={{
						background:
							"linear-gradient(90deg, #004096 0%, #470096 100%)",
					}}
				>
					Most Popular
				</div>
			)}

			<span className={priceClassName}>
				${plan.price} <span className={priceCycleClassName}>Per month</span>
			</span>

			<p className={titleClassName}>{plan.name}</p>

			<div className="flex flex-col gap-4">
				<p className={whatsIncludedClassName}>What's included:</p>
				<PlanFeatures
					description={plan.description}
					monthlyEntries={plan.monthlyEntries}
					weeklyEntries={plan.weeklyEntries}
					isSelected={isSelected}
				/>
			</div>

			{isCurrentPlan ? (
				<div className="flex h-[56px] flex-col justify-end">
					<p className="web-body-medium text-gray-700">
						Your current plan{" "}
						{currentSubscription?.status === "canceling" &&
							"(Marked for cancelation)"}
					</p>
				</div>
			) : (
				<Button
					variant={isSelected ? "white" : "secondary"}
					disabled={
						currentSubscription != undefined &&
						currentSubscription?.status !== "active"
					}
					onClick={() => onSelect(plan)}
				>
					{buttonText}
				</Button>
			)}
		</div>
	);
}
